/**
 * Étape 3 — Revenus du foyer fiscal.
 *
 * Calcule la tranche Anah en temps réel (très modestes / modestes /
 * intermédiaires / supérieurs) à partir du RFR et de la taille du foyer.
 * Affiche le taux MaPrimeRénov’ applicable instantanément.
 *
 * Skippée automatiquement pour les profils copropriété / locataire
 * (forfait collectif ou pas de MPR).
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import {
  ArrowRight,
  HelpCircle,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import {
  computeRevenuBracket,
  getPlafondsForHousehold,
  REVENU_BRACKET_COLORS,
  REVENU_BRACKET_LABELS,
  REVENU_BRACKET_MPR_TAUX,
  type AnahZone,
} from '../../../lib/zoning';
import { useAidsStore } from './useAidsStore';
import { fmtEuro } from './compute';

export function Step3Revenus() {
  const profile = useAidsStore((s) => s.profile);
  const rfr = useAidsStore((s) => s.rfr);
  const householdSize = useAidsStore((s) => s.householdSize);
  const location = useAidsStore((s) => s.location);
  const setRfr = useAidsStore((s) => s.setRfr);
  const setHouseholdSize = useAidsStore((s) => s.setHouseholdSize);
  const goNext = useAidsStore((s) => s.goNext);

  const anahZone: AnahZone = location?.anahZone ?? 'B2C';
  const isBailleur = profile === 'bailleur';

  const bracket = useMemo(() => {
    if (rfr == null || householdSize == null) return null;
    return computeRevenuBracket(rfr, householdSize, anahZone);
  }, [rfr, householdSize, anahZone]);

  const baseRate = bracket ? REVENU_BRACKET_MPR_TAUX[bracket] : null;
  const displayedRate =
    baseRate != null && isBailleur ? Math.round(baseRate * 0.7) : baseRate;

  const plafonds = useMemo(() => {
    if (householdSize == null) return null;
    return getPlafondsForHousehold(anahZone, householdSize);
  }, [householdSize, anahZone]);

  const canContinue = rfr != null && rfr >= 0 && householdSize != null;

  function handleRfrChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/[^\d]/g, '');
    setRfr(raw === '' ? null : Math.min(parseInt(raw, 10), 999_999));
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        Étape 3 sur 6
      </p>
      <h2 className="mt-2 h-section">
        Vos revenus et votre foyer
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        {isBailleur
          ? "Pour les bailleurs, les taux MPR sont réduits d'environ 30 % par rapport au barème occupant — plafond de 20 000 € par logement."
          : "Le taux MaPrimeRénov’ varie de 30 % à 70 % selon votre revenu fiscal de référence (RFR) et la taille du foyer."}
      </p>

      <div className="mt-6 space-y-5">
        {/* ── RFR ───────────────────────────────────────────── */}
        <div>
          <label
            htmlFor="rfr-input"
            className="flex items-center justify-between gap-2 text-[13px] font-semibold text-ink"
          >
            <span>Revenu fiscal de référence (RFR)</span>
            <span className="text-[11px] font-normal text-stone-400">
              en €/an
            </span>
          </label>
          <div className="relative mt-2">
            <input
              id="rfr-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Ex : 28 000"
              value={rfr != null ? fmtEuro(rfr) : ''}
              onChange={handleRfrChange}
              className="w-full rounded-xl border-2 border-stone-200 bg-white px-4 py-3 pr-12 text-[15px] font-medium tabular-nums text-ink shadow-sm transition-all duration-150 ease-out placeholder:text-stone-300 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-stone-400">
              €
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-stone-400">
            Disponible sur votre dernier avis d'imposition (case « Revenu fiscal
            de référence »).
          </p>
        </div>

        {/* ── Foyer fiscal ──────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between gap-2 text-[13px] font-semibold text-ink">
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-brand-600" strokeWidth={1.8} />
              Personnes au foyer fiscal
            </span>
            <span className="text-[11px] font-normal text-stone-400">
              vous · conjoint · à charge
            </span>
          </div>

          <div role="radiogroup" aria-label="Nombre de personnes au foyer" className="mt-2 flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((n) => {
              const selected = householdSize === n;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setHouseholdSize(n)}
                  className={cn(
                    'flex h-11 min-w-[44px] items-center justify-center rounded-xl border-2 px-3 text-[14px] font-semibold tabular-nums transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-700 shadow-[0_2px_8px_-2px_rgba(16,185,129,0.18)]'
                      : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300 hover:bg-stone-50',
                  )}
                >
                  {n}
                </button>
              );
            })}
            {/* Stepper 6+ */}
            <div
              className={cn(
                'flex items-center gap-1.5 rounded-xl border-2 bg-white px-2 transition-colors duration-150',
                householdSize != null && householdSize >= 6
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-stone-200',
              )}
            >
              <button
                type="button"
                aria-label="Diminuer le nombre de personnes"
                onClick={() =>
                  setHouseholdSize(Math.max(6, (householdSize ?? 6) - 1))
                }
                disabled={(householdSize ?? 0) <= 6}
                className="flex h-10 w-10 items-center justify-center rounded-md text-stone-500 transition-colors disabled:cursor-not-allowed disabled:opacity-30 enabled:hover:bg-stone-100 enabled:hover:text-ink"
              >
                <span className="text-lg leading-none">−</span>
              </button>
              <span
                className={cn(
                  'min-w-[2.5ch] text-center text-[14px] font-semibold tabular-nums',
                  householdSize != null && householdSize >= 6
                    ? 'text-brand-700'
                    : 'text-stone-400',
                )}
              >
                {householdSize != null && householdSize >= 6
                  ? householdSize
                  : '6+'}
              </span>
              <button
                type="button"
                aria-label="Augmenter le nombre de personnes"
                onClick={() =>
                  setHouseholdSize(Math.max(6, (householdSize ?? 5) + 1))
                }
                className="flex h-10 w-10 items-center justify-center rounded-md text-stone-500 transition-colors hover:bg-stone-100 hover:text-ink"
              >
                <span className="text-lg leading-none">+</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Feedback live : tranche déduite ───────────────── */}
        <AnimatePresence mode="wait">
          {bracket && plafonds && displayedRate != null && (
            <motion.div
              key={`bracket-${bracket}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3, ease: easeStripe }}
              className="overflow-hidden rounded-2xl border border-brand-200/70 bg-gradient-to-br from-brand-50 via-white to-accent-50/30 p-4 shadow-[0_4px_24px_-12px_rgba(16,185,129,0.25)]"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-inset ring-brand-200/60">
                  <TrendingUp
                    className="h-5 w-5 text-brand-700"
                    strokeWidth={1.7}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
                    Votre tranche Anah
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-[11px] font-bold',
                        REVENU_BRACKET_COLORS[bracket],
                      )}
                    >
                      {REVENU_BRACKET_LABELS[bracket]}
                    </span>
                    <span className="text-[12px] text-stone-500">
                      {householdSize}{' '}
                      {householdSize === 1 ? 'personne' : 'personnes'} ·{' '}
                      {fmtEuro(rfr!)} € de RFR
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <motion.p
                    key={`rate-${displayedRate}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25, ease: easeStripe }}
                    className="font-display text-2xl font-bold tabular-nums text-brand-700"
                  >
                    {displayedRate}%
                  </motion.p>
                  <p className="text-[10px] text-stone-400">
                    {isBailleur ? 'Taux Bailleur' : 'Taux MPR'}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── CTA ───────────────────────────────────────────── */}
        <Button
          variant="brand"
          size="xl"
          disabled={!canContinue}
          onClick={goNext}
          className="w-full gap-2"
        >
          Continuer
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-lg bg-stone-50 p-3">
        <HelpCircle
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"
          strokeWidth={1.8}
          aria-hidden
        />
        <p className="text-[11px] leading-relaxed text-stone-500">
          Barème Anah 2026 — vos données restent côté navigateur jusqu'à la
          dernière étape.{' '}
          <a
            href="https://france-renov.gouv.fr/aides/mpr"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-700 hover:underline"
          >
            Vérifier mes plafonds officiels →
          </a>
        </p>
      </div>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <ShieldCheck className="h-3 w-3" strokeWidth={1.8} />
        Calcul local · aucune donnée envoyée à ce stade
      </p>
    </div>
  );
}
